import React, { Suspense, lazy, useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import Landing from './pages/Landing';
import Home from './pages/marketing/Home';
import SeatingGuide from './pages/marketing/SeatingGuide';
import RetroGuide from './pages/marketing/RetroGuide';
import BudgetGuide from './pages/marketing/BudgetGuide';
import DrinksTool from './pages/marketing/DrinksTool';
import RsvpGuide from './pages/marketing/RsvpGuide';
import QrGuide from './pages/marketing/QrGuide';
import FormGuide from './pages/marketing/FormGuide';
import GoogleFormGuide from './pages/marketing/GoogleFormGuide';
import GuestListGuide from './pages/marketing/GuestListGuide';
import Guides from './pages/marketing/Guides';
import AuthorPage from './pages/marketing/Author';
import InvitationTimingGuide from './pages/marketing/InvitationTimingGuide';
import ArticleRoute from './pages/marketing/articles/ArticleRoute';
import { ARTICLES } from './pages/marketing/articles';
import ScrollToTop from './components/ScrollToTop';
import { usePageTitle } from './pages/marketing/common/usePageTitle';

import { isMarketing, syncManifestTenant } from './tenant';
import { applyPrimaryColor, ensureFontsLoaded, setFavicon, applyFonts } from './personalization';
import { useLang } from './i18n';
import { trackSignupClicks } from './analytics';
import './App.css';
import './styles/theme.css';

// Pages chargées à la demande : l'accueil (vitrine ou site du couple) ne télécharge
// ni l'admin ni l'inscription. Home et Landing restent dans le bundle principal
// (Home a son contenu pré-rendu par le serveur, qu'un fallback ferait clignoter ;
// même chose pour les pages outils/guides comme SeatingGuide).
const Rsvp = lazy(() => import('./pages/rsvp'));
const Admin = lazy(() => import('./pages/admin'));
const DayplanTeam = lazy(() => import('./pages/DayplanTeam'));
const AppEntry = lazy(() => import('./pages/marketing/AppEntry'));
const Login = lazy(() => import('./pages/marketing/Login'));
const Signup = lazy(() => import('./pages/marketing/signup'));
const LegalPage = lazy(() => import('./pages/marketing/Legal'));
const Backoffice = lazy(() => import('./pages/backoffice'));

// Titre de l'onglet du site vitrine selon la page
const MarketingTitle: React.FC = () => {
  usePageTitle();
  return null;
};

function App() {
  const marketing = isMarketing();
  const { setLang } = useLang();

  // Sur un sous-domaine, applique la couleur + le titre du couple
  useEffect(() => {
    if (marketing) {
      setFavicon(); // couleur de l'app (marque)
      return trackSignupClicks();
    }
    syncManifestTenant();
    fetch('/api/account')
      .then((res) => (res.ok ? res.json() : null))
      .then((data) => {
        if (!data) return;
        applyPrimaryColor(data.primaryColor);
        setFavicon(data.primaryColor); // couleur du couple
        if (data.language === 'fr' || data.language === 'en') setLang(data.language);
        if (data.fontFamily || data.bodyFont) ensureFontsLoaded();
        applyFonts(data.fontFamily, data.bodyFont);
        const names = `${data.partner1} & ${data.partner2}`;
        document.title = `Mariage de ${names}`;
        const meta = document.querySelector('meta[name="description"]');
        if (meta) meta.setAttribute('content', `Répondez à l'invitation au mariage de ${names}`);
      })
      .catch(() => {});
  }, [marketing, setLang]);

  return (
    <Router>
      <ScrollToTop />
      <Suspense fallback={null}>
        {marketing ? (
          // www / apex : site vitrine + inscription
          <>
            <MarketingTitle />
            <Routes>
              <Route path="/" element={<Home />} />
              <Route path="/plan-de-table-mariage" element={<SeatingGuide />} />
              <Route path="/retroplanning-mariage" element={<RetroGuide />} />
              <Route path="/budget-mariage" element={<BudgetGuide />} />
              <Route path="/calculateur-boissons-mariage" element={<DrinksTool />} />
              <Route path="/rsvp-mariage" element={<RsvpGuide />} />
              <Route path="/qr-code-faire-part-mariage" element={<QrGuide />} />
              <Route path="/formulaire-reponse-mariage" element={<FormGuide />} />
              <Route path="/google-form-mariage" element={<GoogleFormGuide />} />
              <Route path="/guides" element={<Guides />} />
              <Route path="/estelle-de-truchis-de-varennes" element={<AuthorPage />} />
              <Route path="/liste-invites-mariage" element={<GuestListGuide />} />
              <Route path="/quand-envoyer-faire-part-mariage" element={<InvitationTimingGuide />} />
              {ARTICLES.map((a) => (
                <Route key={a.slug} path={`/${a.slug}`} element={<ArticleRoute meta={a} />} />
              ))}
              <Route path="/backoffice/*" element={<Backoffice />} />
              <Route path="/login" element={<Login />} />
              <Route path="/app" element={<AppEntry />} />
              <Route path="/signup" element={<Signup />} />
              <Route path="/mentions-legales" element={<LegalPage page="mentions" />} />
              <Route path="/confidentialite" element={<LegalPage page="privacy" />} />
              <Route path="/conditions" element={<LegalPage page="terms" />} />
              <Route path="*" element={<Navigate to="/" replace />} />
            </Routes>
          </>
        ) : (
          // [couple].please-rsvp.com : espace mariage
          <Routes>
            <Route path="/" element={<Landing />} />
            <Route path="/rsvp" element={<Rsvp />} />
            <Route path="/admin" element={<Admin />} />
            <Route path="/admin/:tab" element={<Admin />} />
            <Route path="/deroule/:token" element={<DayplanTeam />} />
            <Route path="*" element={<Navigate to="/" replace />} />
          </Routes>
        )}
      </Suspense>
    </Router>
  );
}

export default App;
